import * as React from "react";
import { EllipsisIcon, LogOutIcon, SunMoonIcon, SunIcon, MoonIcon } from "../icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useHostAdapter } from "../../platform";
import { useAddInVersion } from "../../hooks/useAddInVersion";
import { useColorPreference } from "../../hooks/useColorPreference";
import { ColorPreference, parseColorPreference } from "../../platform/shared/officeTheme";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

const APPEARANCE_OPTIONS: { value: ColorPreference; label: string; icon: React.ReactNode }[] = [
  { value: "system", label: "System", icon: <SunMoonIcon className="size-[15px]" /> },
  { value: "light", label: "Light", icon: <SunIcon className="size-[15px]" /> },
  { value: "dark", label: "Dark", icon: <MoonIcon className="size-[15px]" /> },
];

/**
 * The overflow menu at the right of the header: the appearance switch, log out, and a
 * line naming the add-in and its version. Both used to be in the header itself -- a logout icon that
 * was the most prominent thing on the right, and a version that only appeared on
 * hovering the logo -- and neither is something a user needs more than rarely.
 *
 * Appearance is a segmented control rather than three menu items because it is a
 * setting with a current value, not an action; picking one leaves the menu open so the
 * pane can be seen changing behind it. "System" follows Office (see useOfficeThemeSync).
 */
const HeaderMenu = () => {
  const { logout } = useAuth0();
  const host = useHostAdapter();
  const version = useAddInVersion();
  const { preference, setPreference } = useColorPreference();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="More"
          title="More"
          className="text-muted-foreground"
        >
          <EllipsisIcon />
        </Button>
      </DropdownMenuTrigger>
      {/* Wide enough for three labelled segments: "System" with its icon and padding is
          about 80px, and a narrower menu makes the labels wrap inside the pills. */}
      <DropdownMenuContent align="end" className="min-w-[264px]">
        {/* Not a menu item, so the menu's arrow-key and type-ahead handling would treat
            the segments' key presses as its own. Escape and Tab still reach the menu. */}
        <div
          role="group"
          aria-labelledby="header-menu-appearance"
          onKeyDown={(event) => {
            if (event.key !== "Escape" && event.key !== "Tab") event.stopPropagation();
          }}
          className="px-2 pt-1.5 pb-2.5"
        >
          <span
            id="header-menu-appearance"
            className="mb-1.5 block text-caption text-muted-foreground"
          >
            Appearance
          </span>
          <ToggleGroup
            type="single"
            fullWidth
            value={preference}
            aria-labelledby="header-menu-appearance"
            onValueChange={(next) => {
              // An exclusive group reports "" when the selected segment is pressed again.
              if (next) setPreference(parseColorPreference(next));
            }}
          >
            {APPEARANCE_OPTIONS.map((option) => (
              <ToggleGroupItem key={option.value} value={option.value} className="px-2">
                {option.icon}
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => logout({ logoutParams: { returnTo: undefined }, openUrl: false })}
        >
          <LogOutIcon />
          Log out
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="flex flex-col px-2 py-1.5 text-caption">
          <span className="text-muted-foreground">Jylo for {host.displayName}</span>
          {version && <span className="text-disabled">Version {version}</span>}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HeaderMenu;
